import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Sparkles, HelpCircle, MessageSquare, AlertTriangle,
  Footprints, Clock, Loader2, RotateCcw, ChevronDown,
  ChevronUp, CheckCircle, ArrowLeft, ArrowRight, FileText,
  Plus, X, ShieldCheck, Save
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { invokeFunction } from "../../lib/invokeFunction";
import ExpertGoalSetting from "./ExpertGoalSetting";
import BriefItemRow from "./BriefItemRow";

function hashGoals(goals) {
  if (!goals) return "";
  const str = [
    goals.session_goal || "",
    goals.session_deliverable || "",
    goals.expert_questions || ""
  ].join("|").trim();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + ch;
    hash |= 0;
  }
  return String(hash);
}

export default function PrepBriefView({ booking, expertProfile, onBriefGenerated }) {
  const [prepBrief, setPrepBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeView, setActiveView] = useState("input");
  const [expandedSections, setExpandedSections] = useState({
    questions: true, points: true, pains: false, steps: true, agenda: true
  });

  const [includedItems, setIncludedItems] = useState({});
  const [addingTo, setAddingTo] = useState(null);
  const [newItemText, setNewItemText] = useState("");

  const goalsHashAtGeneration = useRef("");
  const currentGoalsRef = useRef(null);
  const [goalsChanged, setGoalsChanged] = useState(false);

  const [briefStatus, setBriefStatus] = useState("draft");
  const [approving, setApproving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    const loadSaved = async () => {
      try {
        const { data } = await supabase
          .from("session_materials")
          .select("prep_brief, prep_brief_status, prep_brief_included, prep_brief_approved_at")
          .eq("booking_id", booking.id)
          .maybeSingle();

        if (data?.prep_brief && Object.keys(data.prep_brief).length > 0) {
          setPrepBrief(data.prep_brief);
          setBriefStatus(data.prep_brief_status || "draft");
          if (data.prep_brief_included && Object.keys(data.prep_brief_included).length > 0) {
            setIncludedItems(data.prep_brief_included);
          } else {
            initIncludedItems(data.prep_brief);
          }
          if (onBriefGenerated) onBriefGenerated(data.prep_brief);
        }
      } catch (err) {
        console.warn("Failed to load saved prep brief:", err);
      } finally {
        setInitialLoading(false);
      }
    };
    loadSaved();
  }, [booking.id]);

  const saveBriefToDb = useCallback(async (brief, included, status) => {
    if (!brief || !booking.id) return;
    setSaving(true);
    try {
      const payload = {
        prep_brief: brief,
        prep_brief_included: included || {},
        prep_brief_status: status || "draft",
        updated_at: new Date().toISOString()
      };
      if (status === "approved") {
        payload.prep_brief_approved_at = new Date().toISOString();
      }

      const { data: existing } = await supabase
        .from("session_materials")
        .select("id")
        .eq("booking_id", booking.id)
        .maybeSingle();

      if (existing) {
        await supabase
          .from("session_materials")
          .update(payload)
          .eq("id", existing.id);
      } else {
        await supabase
          .from("session_materials")
          .insert({
            ...payload,
            booking_id: booking.id,
            expert_id: booking.expertId,
            client_id: booking.clientId,
            expert_name: booking.expertName || "",
            client_name: booking.clientName || "",
            session_date: booking.date || "",
            session_topic: booking.topic || ""
          });
      }
      setLastSaved(new Date());
    } catch (err) {
      console.warn("Failed to save prep brief:", err);
    } finally {
      setSaving(false);
    }
  }, [booking]);

  const scheduleSave = useCallback((brief, included) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveBriefToDb(brief, included, "draft");
    }, 2000);
  }, [saveBriefToDb]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleGoalSet = (goalsData) => {
    currentGoalsRef.current = goalsData;
    if (prepBrief) {
      const currentHash = hashGoals(goalsData);
      setGoalsChanged(currentHash !== goalsHashAtGeneration.current);
    }
  };

  const initIncludedItems = (brief) => {
    const map = {};
    const keys = ["key_questions", "talking_points", "pain_points", "actionable_steps"];
    keys.forEach((k) => {
      if (Array.isArray(brief[k])) {
        map[k] = brief[k].map(() => true);
      }
    });
    setIncludedItems(map);
  };

  const toggleItem = (briefKey, index) => {
    setIncludedItems((prev) => {
      const arr = [...(prev[briefKey] || [])];
      arr[index] = !arr[index];
      const next = { ...prev, [briefKey]: arr };
      scheduleSave(prepBrief, next);
      return next;
    });
    if (briefStatus === "approved") setBriefStatus("draft");
  };

  const editItem = (briefKey, index, newValue) => {
    setPrepBrief((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[briefKey] || [])];
      arr[index] = newValue;
      updated[briefKey] = arr;
      scheduleSave(updated, includedItems);
      return updated;
    });
    if (briefStatus === "approved") setBriefStatus("draft");
  };

  const deleteItem = (briefKey, index) => {
    let nextBrief;
    let nextIncluded;
    setPrepBrief((prev) => {
      const updated = { ...prev };
      updated[briefKey] = (updated[briefKey] || []).filter((_, i) => i !== index);
      nextBrief = updated;
      return updated;
    });
    setIncludedItems((prev) => {
      const arr = (prev[briefKey] || []).filter((_, i) => i !== index);
      nextIncluded = { ...prev, [briefKey]: arr };
      return nextIncluded;
    });
    if (briefStatus === "approved") setBriefStatus("draft");
    setTimeout(() => {
      if (nextBrief) scheduleSave(nextBrief, nextIncluded);
    }, 0);
  };

  const addItem = (briefKey) => {
    if (!newItemText.trim()) return;
    let nextBrief;
    let nextIncluded;
    setPrepBrief((prev) => {
      const updated = { ...prev };
      updated[briefKey] = [...(updated[briefKey] || []), newItemText.trim()];
      nextBrief = updated;
      return updated;
    });
    setIncludedItems((prev) => {
      const arr = [...(prev[briefKey] || []), true];
      nextIncluded = { ...prev, [briefKey]: arr };
      return nextIncluded;
    });
    setNewItemText("");
    setAddingTo(null);
    if (briefStatus === "approved") setBriefStatus("draft");
    setTimeout(() => {
      if (nextBrief) scheduleSave(nextBrief, nextIncluded);
    }, 0);
  };

  const sessionContext = {
    clientName: booking.clientName || "",
    topic: booking.topic || "",
    clientTask: "",
    expertGoal: currentGoalsRef.current?.session_goal || ""
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError("");
    try {
      const previousNotes = [];
      if (booking.clientId && booking.expertId) {
        const { data: notes } = await supabase
          .from("consultation_notes")
          .select("summary, recommendations, next_focus, session_date, session_topic")
          .eq("expert_id", booking.expertId)
          .eq("client_id", booking.clientId)
          .order("created_at", { ascending: false })
          .limit(5);
        if (notes) previousNotes.push(...notes);
      }

      let expertArticles = [];
      try {
        const { data: articles } = await supabase
          .from("articles")
          .select("title")
          .eq("author_id", booking.expertId)
          .eq("status", "published")
          .limit(5);
        if (articles) expertArticles = articles;
      } catch (e) {
        console.warn("Could not fetch articles:", e);
      }

      let clientBrief = null;
      try {
        const { data } = await supabase
          .from("client_session_briefs")
          .select("focus_area, task_description, current_situation, expected_outcome")
          .eq("booking_id", booking.id)
          .maybeSingle();
        if (data) clientBrief = data;
      } catch (e) {
        console.warn("Could not fetch client brief:", e);
      }

      let expertGoals = null;
      try {
        const { data } = await supabase
          .from("expert_session_goals")
          .select("session_goal, session_deliverable, expert_questions")
          .eq("booking_id", booking.id)
          .maybeSingle();
        if (data) expertGoals = data;
      } catch (e) {
        console.warn("Could not fetch expert goals:", e);
      }

      goalsHashAtGeneration.current = hashGoals(expertGoals);
      currentGoalsRef.current = expertGoals;

      const { data, error: fnError } = await invokeFunction("generate-prep-brief", {
        body: {
          expertProfile: {
            name: expertProfile?.name || "",
            title: expertProfile?.title || "",
            bio: expertProfile?.bio || "",
            specializations: expertProfile?.specializations || []
          },
          clientName: booking.clientName || "Клиент",
          sessionTopic: booking.topic || "Общая консультация",
          sessionDate: booking.date || "",
          previousSessions: previousNotes,
          expertArticles,
          clientBrief,
          expertGoals
        }
      });

      if (fnError) throw new Error(fnError.message || "Ошибка генерации");
      if (data?.prepBrief) {
        setPrepBrief(data.prepBrief);
        const newIncluded = {};
        const keys = ["key_questions", "talking_points", "pain_points", "actionable_steps"];
        keys.forEach((k) => {
          if (Array.isArray(data.prepBrief[k])) {
            newIncluded[k] = data.prepBrief[k].map(() => true);
          }
        });
        setIncludedItems(newIncluded);
        setGoalsChanged(false);
        setBriefStatus("draft");
        setActiveView("result");
        if (clientBrief?.task_description) {
          sessionContext.clientTask = clientBrief.task_description;
        }
        await saveBriefToDb(data.prepBrief, newIncluded, "draft");
        if (onBriefGenerated) onBriefGenerated(data.prepBrief);
      } else {
        throw new Error("AI не вернул результат");
      }
    } catch (err) {
      setError(err.message || "Ошибка при генерации Prep Brief");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    try {
      await saveBriefToDb(prepBrief, includedItems, "approved");
      setBriefStatus("approved");
    } catch (err) {
      console.error("Approve error:", err);
    } finally {
      setApproving(false);
    }
  };

  const handleViewResult = () => {
    setActiveView("result");
  };

  const handleBackToInput = () => {
    setActiveView("input");
  };

  const sections = prepBrief ? [
    {
      key: "questions",
      title: "Ключевые вопросы для обсуждения",
      icon: HelpCircle,
      color: "text-blue-400",
      bgColor: "bg-blue-500/10",
      items: prepBrief.key_questions,
      briefKey: "key_questions"
    },
    {
      key: "points",
      title: "Рекомендуемые тезисы",
      icon: MessageSquare,
      color: "text-primary-400",
      bgColor: "bg-primary-500/10",
      items: prepBrief.talking_points,
      briefKey: "talking_points"
    },
    {
      key: "pains",
      title: "Потенциальные боли клиента",
      icon: AlertTriangle,
      color: "text-amber-400",
      bgColor: "bg-amber-500/10",
      items: prepBrief.pain_points,
      briefKey: "pain_points"
    },
    {
      key: "steps",
      title: "Предложения по шагам",
      icon: Footprints,
      color: "text-accent-400",
      bgColor: "bg-accent-500/10",
      items: prepBrief.actionable_steps,
      briefKey: "actionable_steps"
    }
  ] : [];

  if (initialLoading) {
    return (
      <div id="prep-brief-view_init-loading" className="p-6 text-center">
        <Loader2 className="w-6 h-6 text-primary-400 animate-spin mx-auto" />
      </div>
    );
  }

  if (loading) {
    return (
      <motion.div
        id="prep-brief-view_loading"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-8 rounded-md bg-neutral-900 border border-neutral-800 text-center"
      >
        <Loader2 className="w-10 h-10 text-primary-400 animate-spin mx-auto mb-4" />
        <p className="text-lg font-extralight text-neutral-300 mb-2">
          AI анализирует данные и готовит рекомендации...
        </p>
        <p className="text-lg font-extralight text-neutral-500">
          Это может занять 10-20 секунд
        </p>
      </motion.div>
    );
  }

  if (activeView === "result" && prepBrief) {
    return (
      <motion.div
        id="prep-brief-view_content"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="space-y-4"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={handleBackToInput}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-neutral-400 hover:text-white hover:bg-neutral-800/50 transition-colors flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-lg font-extralight">К вводным</span>
            </button>
            <div className="hidden sm:block w-px h-6 bg-neutral-800 flex-shrink-0" />
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-md bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-primary-400" />
              </div>
              <div className="min-w-0">
                <h3 className="text-xl font-medium text-white">Prep Brief</h3>
                <p className="text-lg font-extralight text-neutral-500 truncate">
                  {booking.clientName} — {booking.topic || "Без темы"}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {saving && (
              <span className="flex items-center gap-1.5 text-neutral-500 text-lg font-extralight">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Сохранение...
              </span>
            )}
            {!saving && lastSaved && (
              <span className="text-lg font-extralight text-neutral-600">
                Сохранено
              </span>
            )}
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-md border border-neutral-700 text-neutral-300 hover:text-white hover:border-neutral-500 text-lg font-extralight transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="hidden sm:inline">Перегенерировать</span>
              <span className="sm:hidden">Заново</span>
            </button>
          </div>
        </div>

        {briefStatus === "approved" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 p-3 rounded-md bg-green-500/10 border border-green-500/20"
          >
            <ShieldCheck className="w-5 h-5 text-green-400 flex-shrink-0" />
            <p className="text-lg font-extralight text-green-300">
              План встречи утвержден. Редактирование переведет его обратно в черновик.
            </p>
          </motion.div>
        )}

        {prepBrief.session_agenda?.length > 0 && (
          <BriefSection
            title="Предлагаемый план сессии"
            icon={Clock}
            color="text-green-400"
            bgColor="bg-green-500/10"
            expanded={expandedSections.agenda}
            onToggle={() => toggleSection("agenda")}
          >
            <div className="space-y-2">
              {prepBrief.session_agenda.map((item, i) => (
                <div key={i} className="flex flex-col md:flex-row md:items-start gap-1 md:gap-3 py-2.5 first:pt-0 last:pb-0">
                  <span className="text-lg font-extralight text-neutral-200 md:order-2">
                    {item.topic}
                  </span>
                  <span className="text-lg font-extralight text-green-400 flex-shrink-0 md:w-24 md:order-1">
                    {item.time}
                  </span>
                </div>
              ))}
            </div>
          </BriefSection>
        )}

        {sections.map((section) => (
          section.items?.length > 0 && (
            <BriefSection
              key={section.key}
              title={section.title}
              icon={section.icon}
              color={section.color}
              bgColor={section.bgColor}
              expanded={expandedSections[section.key]}
              onToggle={() => toggleSection(section.key)}
              onAdd={() => {
                setAddingTo(addingTo === section.briefKey ? null : section.briefKey);
                setNewItemText("");
              }}
              showAdd
            >
              <ul className="space-y-0">
                {section.items.map((item, i) => (
                  <BriefItemRow
                    key={`${section.briefKey}-${i}`}
                    item={item}
                    index={i}
                    color={section.color}
                    included={includedItems[section.briefKey]?.[i] !== false}
                    onToggle={() => toggleItem(section.briefKey, i)}
                    onEdit={(idx, val) => editItem(section.briefKey, idx, val)}
                    onDelete={(idx) => deleteItem(section.briefKey, idx)}
                    sectionKey={section.key}
                    sessionContext={sessionContext}
                  />
                ))}
              </ul>

              <AnimatePresence>
                {addingTo === section.briefKey && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="flex items-start gap-2 mt-3 pt-3 border-t border-neutral-800/50">
                      <textarea
                        value={newItemText}
                        onChange={(e) => setNewItemText(e.target.value)}
                        rows={2}
                        autoFocus
                        placeholder="Добавить свой пункт..."
                        className="flex-1 px-3 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500 resize-none"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            addItem(section.briefKey);
                          }
                          if (e.key === "Escape") {
                            setAddingTo(null);
                            setNewItemText("");
                          }
                        }}
                      />
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => addItem(section.briefKey)}
                          disabled={!newItemText.trim()}
                          className="px-3 py-2 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 text-white text-lg font-extralight transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setAddingTo(null); setNewItemText(""); }}
                          className="px-3 py-2 rounded-md hover:bg-neutral-700 text-neutral-400 text-lg font-extralight transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </BriefSection>
          )
        ))}

        <div className="pt-2">
          {briefStatus !== "approved" ? (
            <button
              onClick={handleApprove}
              disabled={approving}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-green-600 hover:bg-green-500 disabled:bg-neutral-700 text-white text-lg font-medium transition-colors"
            >
              {approving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <ShieldCheck className="w-5 h-5" />
              )}
              {approving ? "Утверждение..." : "Утвердить план встречи"}
            </button>
          ) : (
            <div className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-green-500/10 border border-green-500/20">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-lg font-medium text-green-400">
                План утвержден
              </span>
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      id="prep-brief-view_input"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <ExpertGoalSetting
        booking={booking}
        onGoalSet={handleGoalSet}
      />

      {prepBrief && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-md border border-primary-600/30 bg-primary-950/20"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-md bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-primary-400" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-lg font-medium text-white">
                    Prep Brief уже сгенерирован
                  </p>
                  {briefStatus === "approved" && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-500/10 text-green-400 text-lg font-extralight">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      Утвержден
                    </span>
                  )}
                </div>
                <p className="text-lg font-extralight text-neutral-500">
                  {goalsChanged
                    ? "Вводные изменились — можно перегенерировать"
                    : "Вводные не менялись — можно просмотреть результат"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {goalsChanged && (
                <button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20 text-lg font-extralight transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Обновить
                </button>
              )}
              <button
                onClick={handleViewResult}
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-medium transition-colors"
              >
                <ArrowRight className="w-4 h-4" />
                К результату
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {!prepBrief && (
        <motion.div
          id="prep-brief-view_empty"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-md bg-gradient-to-br from-primary-950/40 to-accent-950/20 border border-primary-600/20"
        >
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-600/20 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-primary-400" />
            </div>
            <h3 className="text-2xl font-medium text-white mb-2">
              AI-подготовка к сессии
            </h3>
            <p className="text-lg font-extralight text-neutral-400 mb-6 max-w-md mx-auto">
              AI синтезирует план на основе вводных от клиента, вашего целеполагания и истории сессий
            </p>
            {error && (
              <div className="flex items-center gap-3 p-4 rounded-md bg-red-500/10 border border-red-500/20 mb-4 max-w-md mx-auto">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-lg font-extralight text-red-300">{error}</p>
              </div>
            )}
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
            >
              <Sparkles className="w-5 h-5" />
              Сгенерировать Prep Brief
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

function BriefSection({ title, icon: Icon, color, bgColor, expanded, onToggle, children, onAdd, showAdd }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/50 overflow-hidden">
      <div className="flex items-center justify-between">
        <button
          onClick={onToggle}
          className="flex-1 flex items-center gap-3 p-4 hover:bg-neutral-800/30 transition-colors"
        >
          <div className={`w-8 h-8 rounded-md ${bgColor} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-4 h-4 ${color}`} />
          </div>
          <span className="text-lg font-medium text-white">{title}</span>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-neutral-500 ml-auto" />
          ) : (
            <ChevronDown className="w-5 h-5 text-neutral-500 ml-auto" />
          )}
        </button>
        {showAdd && expanded && (
          <button
            onClick={(e) => { e.stopPropagation(); onAdd(); }}
            className="mr-4 p-1.5 rounded-md hover:bg-neutral-700/50 text-neutral-500 hover:text-primary-400 transition-colors"
            title="Добавить свой пункт"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}